import type { Sticker, GameType, StickerCollection, StickerCollectionMeta } from '../types';

// Import data from JSON files (Vite handles JSON imports natively)
import stickersJson from './stickers.json';
import collectionsJson from './sticker-collections.json';

// JSON shape interfaces (for cleaner type assertions on imported JSON)
interface CollectionJsonEntry {
  id: string;
  emoji: string;
  unlockAfter: number;
}

interface StickerJsonEntry {
  id: string;
  name: string;
  image: string;
  collection: string;
  funFact: string;
  emoji: string;
}

// Collection metadata - defines unlock order and requirements
// Names are stored as i18n keys (stickers:collections.{id})
export const stickerCollections: StickerCollectionMeta[] = (collectionsJson as CollectionJsonEntry[]).map(c => ({
  id: c.id as StickerCollection,
  name: `stickers:collections.${c.id}`,
  emoji: c.emoji,
  unlockAfter: c.unlockAfter,
}));

// Build sticker objects from JSON data
export const stickers: Sticker[] = (stickersJson as StickerJsonEntry[]).map(s => ({
  id: s.id,
  name: s.name,
  image: s.image,
  collection: s.collection as StickerCollection,
  game: 'speech' as GameType,
  earned: false,
  funFact: s.funFact,
}));

// Build emoji lookup from JSON data
export const stickerEmojis: Record<string, string> = Object.fromEntries(
  (stickersJson as StickerJsonEntry[]).map(s => [s.id, s.emoji])
);

// Legacy emoji exports (for backwards compatibility)
export const dinoEmojis: Record<string, string> = Object.fromEntries(
  Object.entries(stickerEmojis).filter(([key]) => key.startsWith('dino-'))
);

export const carEmojis: Record<string, string> = Object.fromEntries(
  Object.entries(stickerEmojis).filter(([key]) => key.startsWith('car-'))
);

export const farmEmojis: Record<string, string> = Object.fromEntries(
  Object.entries(stickerEmojis).filter(([key]) => key.startsWith('farm-'))
);

export const seaEmojis: Record<string, string> = Object.fromEntries(
  Object.entries(stickerEmojis).filter(([key]) => key.startsWith('sea-'))
);

// Helper to get stickers by game
export const getStickersByGame = (game: GameType): Sticker[] => {
  return stickers.filter(s => s.game === game);
};

// Helper to get stickers by collection
export const getStickersForCollection = (collection: StickerCollection): Sticker[] => {
  return stickers.filter(s => s.collection === collection);
};

// Helper to get collection metadata
export const getCollectionMeta = (collection: StickerCollection): StickerCollectionMeta | undefined => {
  return stickerCollections.find(c => c.id === collection);
};

// Helper to get a random unearned sticker for a game (from any collection)
export const getRandomUnearnedSticker = (game: GameType, earnedIds: string[]): Sticker | null => {
  const available = stickers.filter(s => s.game === game && !earnedIds.includes(s.id));
  if (available.length === 0) return null;
  const index = Math.floor(Math.random() * available.length);
  return available[index];
};

// Helper to get a random unearned sticker from a specific collection
export const getRandomUnearnedStickerFromCollection = (
  game: GameType, 
  collection: StickerCollection, 
  earnedIds: string[]
): Sticker | null => {
  const available = stickers.filter(
    s => s.game === game && s.collection === collection && !earnedIds.includes(s.id)
  );
  if (available.length === 0) return null;
  const index = Math.floor(Math.random() * available.length);
  return available[index];
};

// Helper to count earned stickers
export const countEarnedStickers = (earnedIds: string[]): number => {
  return earnedIds.length;
};

// Helper to get total stickers
export const getTotalStickers = (): number => {
  return stickers.length;
};

// Helper to determine current collection based on earned count
export const getCurrentCollection = (earnedIds: string[]): StickerCollection => {
  const total = earnedIds.length;
  // Find the last collection that should be unlocked
  for (let i = stickerCollections.length - 1; i >= 0; i--) {
    const collection = stickerCollections[i];
    if (total >= collection.unlockAfter) {
      // Check if this collection still has unearned stickers
      const collectionStickers = getStickersForCollection(collection.id);
      const hasUnearned = collectionStickers.some(s => !earnedIds.includes(s.id));
      if (hasUnearned) {
        return collection.id;
      }
    }
  }
  // Default to the last collection if all are complete
  return stickerCollections[stickerCollections.length - 1].id;
};

// Helper to get sticker by ID
export const getStickerById = (id: string): Sticker | undefined => {
  return stickers.find(s => s.id === id);
};

// Helper to get emoji for a sticker
export const getStickerEmoji = (stickerId: string): string => {
  return stickerEmojis[stickerId] || '⭐';
};

// Helper to get statistics about stickers
export const getStickerStats = () => {
  const collectionCounts: Record<string, number> = {};
  
  stickers.forEach(sticker => {
    collectionCounts[sticker.collection] = (collectionCounts[sticker.collection] || 0) + 1;
  });

  return {
    totalStickers: stickers.length,
    collectionCounts,
    collections: stickerCollections.length,
  };
};
